import { apiRequest } from './http';
import type {
  AllocationStrategy,
  InventoryAccountMapping,
  InventoryProduct,
  InventoryReconciliation,
  InventoryValuation,
  InventoryWarehouse,
  LotStatus,
  ProductType,
  StockBalance,
  StockCount,
  StockLocation,
  StockLot,
  StockMovement,
  StockMovementType,
  StockReservation,
  StockSerial,
  StockTransfer,
  TrackingPolicy,
  UnitConversion,
  UnitDimension,
  UnitOfMeasure,
  ValuationMethod,
} from './types';

/**
 * Inventory write operations. Every command that moves stock carries an Idempotency-Key, so a retried request
 * rejoins the movement it already made instead of posting a second one.
 */
function idempotencyKey(prefix: string): Record<string, string> {
  const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return { 'Idempotency-Key': `${prefix}-${id}` };
}

function send<T>(method: string, path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  return apiRequest<T>(path, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(headers ? { headers } : {}),
  });
}

const post = <T>(path: string, body?: unknown, headers?: Record<string, string>) => send<T>('POST', path, body, headers);
const patch = <T>(path: string, body: unknown) => send<T>('PATCH', path, body);

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  return search.size ? `?${search.toString()}` : '';
}

export interface CreateProductInput {
  sku: string;
  name: string;
  baseUnitCode: string;
  type: ProductType;
  trackingPolicy: TrackingPolicy;
  valuationMethod: ValuationMethod;
  effectiveFrom: string;
  barcode?: string | null;
  categoryCode?: string | null;
  reorderPoint?: number;
  safetyStockQuantity?: number;
}

export interface UpdateProductInput {
  name: string;
  barcode: string | null;
  categoryCode: string | null;
  reorderPoint: number;
  safetyStockQuantity: number;
  trackingPolicy: TrackingPolicy;
  valuationMethod: ValuationMethod;
  baseUnitCode: string;
  version: number;
}

export interface CreateWarehouseInput {
  code: string;
  name: string;
  negativeStockAllowed: boolean;
  branchCode?: string | null;
  address?: string | null;
}

export interface UpdateWarehouseInput {
  name: string;
  negativeStockAllowed: boolean;
  branchCode: string | null;
  address: string | null;
  version: number;
}

export interface CreateLocationInput {
  warehouseId: string;
  code: string;
  name: string;
  zoneCode?: string | null;
  isPickable?: boolean;
  isReceivable?: boolean;
  isQuarantine?: boolean;
  isInTransit?: boolean;
}

export interface CreateLotInput {
  productId: string;
  lotNumber: string;
  manufacturedOn?: string | null;
  expiresOn?: string | null;
  supplierLotNumber?: string | null;
  status?: LotStatus;
}

export interface RecordReceiptInput {
  productId: string;
  warehouseId: string;
  locationId: string | null;
  lotId: string | null;
  serialId: string | null;
  ownerId: string | null;
  stockStatus: string;
  quantity: number;
  unitCode: string;
  unitCost: number;
  currencyCode: string;
  sourceType: string;
  sourceId: string;
  correlationId: string;
  movementType: StockMovementType;
}

export interface RecordIssueInput {
  productId: string;
  warehouseId: string;
  locationId: string | null;
  lotId: string | null;
  serialId: string | null;
  ownerId: string | null;
  stockStatus: string;
  quantity: number;
  unitCode: string;
  currencyCode: string;
  movementType: StockMovementType;
  sourceType: string;
  sourceId: string;
  reservationId: string | null;
  correlationId: string;
}

export interface RecordAdjustmentInput {
  productId: string;
  warehouseId: string;
  quantityDelta: number;
  currencyCode: string;
  reason: string;
  locationId?: string | null;
  lotId?: string | null;
  unitCost?: number | null;
}

export interface CreateReservationInput {
  productId: string;
  warehouseId: string;
  sourceLineId: string;
  sourceType: string;
  quantity: number;
  requiredDate: string;
  allowPartial: boolean;
  strategy?: AllocationStrategy;
  lotId?: string | null;
}

export interface CreateTransferInput {
  number: string;
  sourceWarehouseId: string;
  destinationWarehouseId: string;
  transferDate: string;
  lines: readonly { productId: string; quantity: number }[];
  reference?: string | null;
}

export interface CreateCountInput {
  number: string;
  warehouseId: string;
  countDate: string;
  currencyCode: string;
  productIds: readonly string[];
  isBlind?: boolean;
  description?: string | null;
}

export interface BalanceFilter {
  productId?: string;
  warehouseId?: string;
  category?: string;
  search?: string;
  lowStockOnly?: boolean;
  includeInactive?: boolean;
  includeZeroBalances?: boolean;
}

export const inventoryApi = {
  products: () => apiRequest<readonly InventoryProduct[]>('/products'),

  createProduct: (input: CreateProductInput) => post<InventoryProduct>('/products', input),

  updateProduct: (productId: string, input: UpdateProductInput) =>
    patch<InventoryProduct>(`/products/${productId}`, input),

  setProductActivation: (productId: string, isActive: boolean, effectiveTo: string) =>
    post<InventoryProduct>(`/products/${productId}/activation`, { isActive, effectiveTo }),

  warehouses: () => apiRequest<readonly InventoryWarehouse[]>('/warehouses'),

  createWarehouse: (input: CreateWarehouseInput) => post<InventoryWarehouse>('/warehouses', input),

  updateWarehouse: (warehouseId: string, input: UpdateWarehouseInput) =>
    patch<InventoryWarehouse>(`/warehouses/${warehouseId}`, input),

  setWarehouseActivation: (warehouseId: string, isActive: boolean) =>
    post<InventoryWarehouse>(`/warehouses/${warehouseId}/activation`, { isActive }),

  locations: (warehouseId?: string) =>
    apiRequest<readonly StockLocation[]>(`/inventory/locations${query({ warehouseId })}`),

  createLocation: (input: CreateLocationInput) => post<StockLocation>('/inventory/locations', input),

  setLocationActivation: (locationId: string, isActive: boolean) =>
    post<StockLocation>(`/inventory/locations/${locationId}/activation`, { isActive }),

  lots: (productId?: string) => apiRequest<readonly StockLot[]>(`/inventory/lots${query({ productId })}`),

  createLot: (input: CreateLotInput) => post<StockLot>('/inventory/lots', input),

  changeLotStatus: (lotId: string, status: LotStatus, version: number, recallReference?: string | null) =>
    post<StockLot>(`/inventory/lots/${lotId}/status`, { status, version, recallReference: recallReference ?? null }),

  balances: (filter: BalanceFilter = {}) =>
    apiRequest<readonly StockBalance[]>(`/inventory/balances${query({ ...filter })}`),

  valuation: (warehouseId?: string) =>
    apiRequest<InventoryValuation>(`/inventory/valuation${query({ warehouseId })}`),

  movements: (filter: { productId?: string; warehouseId?: string; type?: StockMovementType; limit?: number } = {}) =>
    apiRequest<readonly StockMovement[]>(`/inventory/movements${query({ ...filter })}`),

  reservations: (openOnly = true) =>
    apiRequest<readonly StockReservation[]>(`/inventory/reservations${query({ openOnly })}`),

  createReservation: (input: CreateReservationInput) =>
    post<StockReservation>('/inventory/reservations', input, idempotencyKey('resv')),

  releaseReservation: (reservationId: string) =>
    post<StockReservation>(`/inventory/reservations/${reservationId}/release`),

  recordReceipt: (input: RecordReceiptInput) =>
    post<StockMovement>('/inventory/receipts', input, idempotencyKey('rcpt')),

  recordIssue: (input: RecordIssueInput) =>
    post<StockMovement>('/inventory/issues', input, idempotencyKey('issue')),

  recordAdjustment: (input: RecordAdjustmentInput) =>
    post<StockMovement>('/inventory/adjustments', input, idempotencyKey('adj')),

  transfers: () => apiRequest<readonly StockTransfer[]>('/inventory/transfers'),

  createTransfer: (input: CreateTransferInput) => post<StockTransfer>('/inventory/transfers', input),

  issueTransfer: (transferId: string) =>
    post<StockTransfer>(`/inventory/transfers/${transferId}/issue`, undefined, idempotencyKey('trf-iss')),

  receiveTransfer: (transferId: string, lines: readonly { lineId: string; quantity: number }[]) =>
    post<StockTransfer>(`/inventory/transfers/${transferId}/receive`, { lines }, idempotencyKey('trf-rcv')),

  cancelTransfer: (transferId: string) => post<StockTransfer>(`/inventory/transfers/${transferId}/cancel`),

  serials: (productId?: string) =>
    apiRequest<readonly StockSerial[]>(`/inventory/serials${query({ productId })}`),

  createSerial: (productId: string, serialNumber: string, lotId?: string | null) =>
    post<StockSerial>('/inventory/serials', { productId, serialNumber, lotId: lotId ?? null }),

  scrapSerial: (serialId: string) => post<StockSerial>(`/inventory/serials/${serialId}/scrap`),

  units: () => apiRequest<readonly UnitOfMeasure[]>('/inventory/units'),

  createUnit: (code: string, name: string, dimension: UnitDimension) =>
    post<UnitOfMeasure>('/inventory/units', { code, name, dimension }),

  updateUnit: (unitId: string, name: string, isActive: boolean, version: number) =>
    patch<UnitOfMeasure>(`/inventory/units/${unitId}`, { name, isActive, version }),

  unitConversions: (productId?: string) =>
    apiRequest<readonly UnitConversion[]>(`/inventory/unit-conversions${query({ productId })}`),

  createUnitConversion: (fromUnitCode: string, toUnitCode: string, factor: number, productId?: string | null) =>
    post<UnitConversion>('/inventory/unit-conversions', {
      fromUnitCode,
      toUnitCode,
      factor,
      productId: productId ?? null,
    }),

  updateUnitConversion: (conversionId: string, factor: number, version: number) =>
    patch<UnitConversion>(`/inventory/unit-conversions/${conversionId}`, { factor, version }),

  accountMappings: () => apiRequest<readonly InventoryAccountMapping[]>('/inventory/account-mappings'),

  saveAccountMapping: (input: {
    inventoryAccountId: string;
    cogsAccountId: string;
    adjustmentAccountId: string;
    categoryCode?: string | null;
  }) => send<InventoryAccountMapping>('PUT', '/inventory/account-mappings', {
    ...input,
    categoryCode: input.categoryCode ?? null,
  }),

  assignProductAccounts: (productId: string, input: {
    version: number;
    inventoryAccountId?: string | null;
    cogsAccountId?: string | null;
    adjustmentAccountId?: string | null;
  }) => send<InventoryProduct>('PUT', `/products/${productId}/accounts`, input),

  reconciliation: (asOf?: string) =>
    apiRequest<InventoryReconciliation>(`/inventory/reconciliation${query({ asOf })}`),

  counts: () => apiRequest<readonly StockCount[]>('/inventory/counts'),

  createCount: (input: CreateCountInput) => post<StockCount>('/inventory/counts', input),

  recordCountLine: (countId: string, lineId: string, countedQuantity: number) =>
    post<StockCount>(`/inventory/counts/${countId}/lines`, { lineId, countedQuantity }),

  submitCount: (countId: string) => post<StockCount>(`/inventory/counts/${countId}/submit`),

  approveCount: (countId: string) => post<StockCount>(`/inventory/counts/${countId}/approve`),

  reopenCount: (countId: string) => post<StockCount>(`/inventory/counts/${countId}/reopen`),

  postCount: (countId: string) =>
    post<StockCount>(`/inventory/counts/${countId}/post`, undefined, idempotencyKey('count')),

  cancelCount: (countId: string) => post<StockCount>(`/inventory/counts/${countId}/cancel`),
};
